import { useEffect, useState } from "react";

// MUI
import { Stack } from "@mui/material";

// Interfaces
import { IAdvisorsRankingConfiguration, IAdvisorsRankingProps } from "./interfaces";

// Helpers
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Constants
const categories = [
	{ label: "Global (all three categories)", category: "global" },
	{ label: "Customer Pay", category: "customer" },
	{ label: "Internal Pay", category: "internal" },
	{ label: "Warranty Pay", category: "warranty" },
];

const mapUpdatedReferences = (references: IAdvisorsRankingConfiguration | null) => {
	const { category = "global", isRankingRO = false } = references ?? {};
	return { category, isRankingRO };
};

const getSelectedCategory = (category = "") => {
	return categories.find((item) => item.category === category) ?? null;
};

export default function AdvisorsRanking(props: Readonly<IAdvisorsRankingProps>) {
	const { category = "global", isRankingRO = false, readOnly = false, saving = false, onActionUpdate = () => {} } = props;
	const [references, setReferences] = useState<IAdvisorsRankingConfiguration | null>(null);

	const update = (data: IAdvisorsRankingConfiguration) => {
		onActionUpdate(data);
	};

	const handleUpdateRanking = (value: boolean) => {
		const updated = {
			...mapUpdatedReferences(references),
			isRankingRO: value,
		};
		setReferences(updated);
		update(updated);
	};

	const handleSelectCategory = (selected: { label: string; category: string }) => {
		const { category: selectedCategory } = selected;
		if (!isSimilarObject({ category: selectedCategory }, { category: references?.category })) {
			const updated = {
				...mapUpdatedReferences(references),
				category: selectedCategory,
			};
			setReferences(updated);
			update(updated);
		}
	};

	useEffect(() => {
		setReferences({ category, isRankingRO });
		return () => {
			setReferences(null);
		};
	}, [category, isRankingRO]);

	return (
		<Stack direction={"row"} spacing={2} alignContent={"center"}>
			<Stack className="min-w-[180px]" alignContent={"center"} justifyContent={"center"}>
				<CustomizedSwitches
					key={"isRankingRO"}
					textRight={true}
					textChecked={"RO/hrs"}
					textUnChecked={"Effective Labor Rate"}
					checked={Boolean(references?.isRankingRO)}
					disabled={readOnly || saving}
					onChange={(value) => handleUpdateRanking(value)}
					tooltip={{
						title: "Change between Effective Labor Rate and RO/hsr.",
						placement: "bottom",
						key: "advisors-ranking-tooltip",
					}}
				/>
			</Stack>

			<Stack direction={"row"} position={"relative"} width={"210px"}>
				<SmartSelector
					showBy={"label"}
					selectBy={"category"}
					options={categories}
					selected={getSelectedCategory(references?.category)}
					inputProps={{
						placeholder: "Account Number(s)",
						classes: `[&_.MuiInputBase-root]:!py-[3px]`,
					}}
					renderTagsCustomProps={{
						classes: "!font-sans",
					}}
					disabled={readOnly || saving}
					loading={saving}
					sx={{ minWidth: "100%" }}
					onChangeAction={(selected) => handleSelectCategory(selected as { label: string; category: string })}
				/>
			</Stack>
		</Stack>
	);
}
